import { type StrapiLocale } from '@nuxtjs/strapi/dist/runtime/types'
import type { Project, Project_Plain, Media, Industry_Plain } from '@webamboos/admin'

export function useProjects() {
  const { find } = useStrapi()
  const { localeProperties } = useI18n()

  const locale = computed(() => localeProperties.value.code as StrapiLocale)

  const fetchBySlug = async (slug: string, industrySlug?: string | string[]) => {
    const options = {
      locale: locale.value,
      populate: {
        features: {
          populate: ['image'],
        },
        highlights: {
          populate: '*',
        },
        technologies: {
          populate: ['image'],
        },
        industries: {
          populate: '*',
        },
        testimonial: {
          populate: ['image', 'partner'],
        },
        image: {
          populate: '*',
        },
      },
      filters: {
        slug,
        ...(industrySlug && {
          industries: {
            slug: industrySlug,
          },
        }),
      },
    }

    const { data } = await find('projects', {
      ...options,
      sort: 'publishedAt:desc',
    })
    const project = data?.[0] as unknown as Project_Plain

    const { data: previousProject } = await find('projects', {
      ...options,
      sort: 'publishedAt:desc',
      filters: {
        ...(industrySlug && {
          industries: {
            slug: industrySlug,
          },
        }),
        publishedAt: {
          $gt: project.publishedAt,
        },
      },
      pagination: { start: 0, limit: 1 },
      fields: ['slug'],
    })

    const { data: nextProject } = await find('projects', {
      ...options,
      sort: 'publishedAt:desc',
      filters: {
        ...(industrySlug && {
          industries: {
            slug: industrySlug,
          },
        }),
        publishedAt: {
          $lt: project.publishedAt,
        },
      },
      pagination: { start: 0, limit: 1 },
      fields: ['slug'],
    })

    return {
      project,
      // @ts-expect-error
      nextSlug: nextProject?.[0]?.slug as string,
      // @ts-expect-error
      previousSlug: previousProject?.[0]?.slug as string,
      meta: {
        title: project.title,
        description: project.meta_description,
        imageUrl: project.image.url,
      },
    }
  }

  const fetch = async (industrySlug?: string | string[], limit?: number) => {
    let industry: Industry_Plain[] = []
    if (industrySlug) {
      const { data: industryData } = await find<Industry_Plain[]>('industries', {
        locale: localeProperties.value.code as StrapiLocale,
        filters: {
          slug: industrySlug,
        },
      })
      industry = industryData as unknown as Industry_Plain[]
    }
    const { data } = await find<Project['attributes']>('projects', {
      locale: locale.value,
      populate: ['industries', 'image', 'technologies'],
      sort: 'publishedAt:desc',
      ...(limit && { pagination: { start: 0, limit } }),
      ...(industrySlug && {
        filters: {
          industries: {
            slug: industrySlug,
          },
        },
      }),
    })

    const projects = data as unknown as Project_Plain[]

    return {
      ...(industrySlug && { industry: industry[0] }),
      projects: projects.map((item) => ({
        ...item,
        industries: item.industries.map((industry) => industry.name),
        industriesSlugs: item.industries.map((industry) => industry.slug),
        image: item.image as unknown as Media,
      })),
    }
  }
  return {
    fetch,
    fetchBySlug,
  }
}
